import React, { useState, useEffect } from 'react';
import { getPhonePrefixes } from '../utils/phoneValidation';
import './PhoneInput.css';

/**
 * PhoneInput component with country code dropdown
 * @param {string} value - Full phone number value (e.g., "+972123456789")
 * @param {function} onChange - Callback when phone number changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} required - Whether field is required
 */
const PhoneInput = ({ value = '', onChange, placeholder = 'Enter phone number', required = false }) => {
    const [prefixes, setPrefixes] = useState([]);
    const [selectedPrefix, setSelectedPrefix] = useState('');
    const [phoneDigits, setPhoneDigits] = useState('');
    const [maxLength, setMaxLength] = useState(15);

    // Load phone prefixes on mount
    useEffect(() => {
        getPhonePrefixes().then(configs => {
            setPrefixes(configs);
            if (configs.length > 0 && !selectedPrefix) {
                setSelectedPrefix(configs[0].prefix);
                setMaxLength(configs[0].length);
            }
        }).catch(err => {
            console.error('Error loading prefixes:', err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Parse existing value when component mounts or value changes from parent
    useEffect(() => {
        if (value && prefixes.length > 0) {
            // Find matching prefix
            const matchedConfig = prefixes.find(p => value.startsWith(p.prefix));
            if (matchedConfig) {
                setSelectedPrefix(matchedConfig.prefix);
                setPhoneDigits(value.substring(matchedConfig.prefix.length));
                setMaxLength(matchedConfig.length);
            } else {
                // If no prefix matches, try to extract digits
                const digitsOnly = value.replace(/\D/g, '');
                setPhoneDigits(digitsOnly);
            }
        }
    }, [value, prefixes]);

    // Handle prefix change
    const handlePrefixChange = (e) => {
        const newPrefix = e.target.value;
        setSelectedPrefix(newPrefix);
        
        // Update max length based on selected prefix
        const config = prefixes.find(p => p.prefix === newPrefix);
        if (config) {
            setMaxLength(config.length);
            // Trim digits if they exceed new max length
            if (phoneDigits.length > config.length) {
                const trimmedDigits = phoneDigits.substring(0, config.length);
                setPhoneDigits(trimmedDigits);
                onChange(newPrefix + trimmedDigits);
            } else {
                onChange(newPrefix + phoneDigits);
            }
        }
    };

    // Handle digits input
    const handleDigitsChange = (e) => {
        let digits = e.target.value.replace(/\D/g, ''); // Only allow digits
        
        // Limit to max length
        if (digits.length > maxLength) {
            digits = digits.substring(0, maxLength);
        }
        
        setPhoneDigits(digits);
        onChange(selectedPrefix + digits);
    };

    return (
        <div className="phone-input-container">
            <select 
                className="phone-prefix-select"
                value={selectedPrefix}
                onChange={handlePrefixChange}
                required={required}
            >
                {prefixes.map((config, index) => (
                    <option key={index} value={config.prefix}>
                        {config.prefix} ({config.country})
                    </option>
                ))}
            </select>
            <input
                type="tel"
                className="phone-digits-input"
                value={phoneDigits}
                onChange={handleDigitsChange}
                placeholder={placeholder}
                required={required}
                maxLength={maxLength}
            />
            <span className="phone-input-hint">
                {maxLength} digits
            </span>
        </div>
    );
};

export default PhoneInput;

